/**
 * Modal affiché après connexion si des documents obligatoires restent à signer.
 * Si au moins un document bloque Prysm'app, le modal n'est pas dismissible
 * (pas de « Plus tard », pas de fermeture backdrop).
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, AlertCircle, X, LogOut } from 'lucide-react';
import { useGroup } from '@/context/GroupContext';
import { useAuth } from '@/context/AuthContext';

function useSafeGroup() {
  try {
    return useGroup();
  } catch {
    return {
      pendingDocuments: [],
      documentsLoaded: false,
      hasBlockingPendingDocuments: false,
    };
  }
}

function sessionDisplayName(user) {
  if (!user) return 'cet utilisateur';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return user.display_name || full || user.username || 'cet utilisateur';
}

export default function PendingDocumentsModal({ onOpenDocuments }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { pendingDocuments, documentsLoaded, hasBlockingPendingDocuments } = useSafeGroup();
  const [dismissed, setDismissed] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  useEffect(() => {
    const check = () => setWelcomeOpen(document.body.hasAttribute('data-welcome-open'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-welcome-open'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setDismissed(false);
  }, [user?.id]);

  useEffect(() => {
    if (hasBlockingPendingDocuments) setDismissed(false);
  }, [hasBlockingPendingDocuments, pendingDocuments.length]);

  if (!documentsLoaded) return null;
  if (!user) return null;
  if (pathname.startsWith('/aide/tutoriel/')) return null;
  if (pendingDocuments.length === 0) return null;
  if (dismissed && !hasBlockingPendingDocuments) return null;
  if (welcomeOpen) return null;

  const blocking = hasBlockingPendingDocuments;

  return (
    <>
      <div
        className={`fixed inset-0 ${blocking ? 'bg-black/50 backdrop-blur-[3px]' : 'bg-black/25 backdrop-blur-[1px]'}`}
        style={{ zIndex: 8500 }}
        onClick={() => {
          if (!blocking) setDismissed(true);
        }}
      />
      <div
        className="fixed inset-0 flex items-end sm:items-center justify-center p-4 sm:p-6 pointer-events-none"
        style={{ zIndex: 8501 }}
      >
        <div className="pointer-events-auto relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-xl p-6 sm:p-8 border-2 border-orange-300 dark:border-orange-700">
          {!blocking && (
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
            >
              <X size={18} />
            </button>
          )}

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
              <AlertCircle size={28} className="text-orange-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 dark:text-white text-lg sm:text-xl leading-snug">
                {blocking
                  ? (pendingDocuments.length === 1
                    ? 'Document à signer pour débloquer Prysm\'app'
                    : 'Documents à signer pour débloquer Prysm\'app')
                  : (pendingDocuments.length === 1
                    ? 'Un document nécessite votre signature'
                    : `${pendingDocuments.length} documents nécessitent votre signature`)}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                {blocking
                  ? 'Signez les documents obligatoires dans Prysm\'Access pour retrouver l\'accès aux autres applications. L\'ancienne validation par clic ne suffit plus : une signature manuscrite est requise.'
                  : 'Des documents obligatoires vous ont été assignés dans Prysm\'Access.'}
              </p>
              <ul className="mt-4 space-y-2">
                {pendingDocuments.slice(0, 5).map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center gap-2.5 text-sm text-gray-800 dark:text-gray-200 bg-orange-50 dark:bg-orange-950/30 rounded-lg px-3 py-2"
                  >
                    <FileText size={16} className="text-orange-500 flex-shrink-0" />
                    <span className="truncate font-medium">{d.titre}</span>
                  </li>
                ))}
                {pendingDocuments.length > 5 && (
                  <li className="text-sm text-gray-400 pl-8">… et {pendingDocuments.length - 5} autre(s)</li>
                )}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    if (!blocking) setDismissed(true);
                    onOpenDocuments?.();
                  }}
                  className="flex-1 px-4 py-3 text-base font-semibold bg-[#f76f4b] hover:bg-[#e55d39] text-white rounded-xl transition-colors shadow-sm"
                >
                  Ouvrir Prysm&apos;Access
                </button>
                {!blocking && (
                  <button
                    type="button"
                    onClick={() => setDismissed(true)}
                    className="px-4 py-3 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Plus tard
                  </button>
                )}
              </div>

              {/* PC partagé : permettre de changer de compte sans signer pour quelqu’un d’autre */}
              <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center leading-relaxed mb-3">
                  Si vous n&apos;êtes pas{' '}
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {sessionDisplayName(user)}
                  </span>
                  , déconnectez-vous
                </p>
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    navigate('/login', { replace: true });
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <LogOut size={16} />
                  Déconnexion
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
